import { useIntl } from 'react-intl';
import { WorkerAttr } from '@/types/worker-attr';
import { useMemo } from 'react';

/**
 * Hook that provides base worker attributes (hardcoded defaults).
 * These are always available regardless of API response.
 * 
 * TODO: Add API integration later to fetch custom attributes
 */
export function useBaseWorkerAttributes(): WorkerAttr[] {
  const intl = useIntl();

  return useMemo(
    () => [
      // Worker attributes
      {
        id: 'lastname',
        label: intl.formatMessage({ defaultMessage: 'worker last name' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'firstname',
        label: intl.formatMessage({ defaultMessage: 'worker first name' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'displayFullName',
        label: intl.formatMessage({ defaultMessage: 'worker name' }),
        dataType: 'string',
        fallback: '',
        tag: 'worker',
      },
      {
        id: 'startDate',
        label: intl.formatMessage({ defaultMessage: 'worker start date' }),
        fallback: '',
        dataType: 'date',
        tag: 'worker',
      },
      {
        id: 'personalEmail.email',
        label: intl.formatMessage({ defaultMessage: 'worker personal email' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'workEmail.email',
        label: intl.formatMessage({ defaultMessage: 'worker work email' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'mobilePhone.number',
        label: intl.formatMessage({ defaultMessage: 'worker phone' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'job.name',
        label: intl.formatMessage({ defaultMessage: 'worker job name' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'locations[0].name',
        label: intl.formatMessage({ defaultMessage: 'worker location name' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'locations[0].street_address',
        label: intl.formatMessage({ defaultMessage: 'worker location address' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'locations[0].city',
        label: intl.formatMessage({ defaultMessage: 'worker location city' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'locations[0].state_code',
        label: intl.formatMessage({ defaultMessage: 'worker location state' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'homeAddress.address1',
        label: intl.formatMessage({ defaultMessage: 'worker home address1' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'homeAddress.address2',
        label: intl.formatMessage({ defaultMessage: 'worker home address2' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'homeAddress.state',
        label: intl.formatMessage({ defaultMessage: 'worker home state' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'homeAddress.city',
        label: intl.formatMessage({ defaultMessage: 'worker home city' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'uuid',
        label: intl.formatMessage({ defaultMessage: 'worker id' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'applicantId',
        label: intl.formatMessage({ defaultMessage: 'worker applicant id' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        id: 'dashboardUrl.url',
        label: intl.formatMessage({ defaultMessage: 'worker portal link' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      {
        // this is a constructed value that should only be used in the context
        // of sending messages to an employer/worker-adjacent user
        id: 'EMPLOYER_ONLY_workerProfileUrl',
        label: intl.formatMessage({ defaultMessage: 'worker profile link' }),
        fallback: '',
        dataType: 'string',
        tag: 'worker',
      },
      // Company attributes
      {
        id: 'address.address1',
        label: intl.formatMessage({ defaultMessage: 'company address1' }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'address.address2',
        label: intl.formatMessage({ defaultMessage: 'company address2' }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'address.city',
        label: intl.formatMessage({ defaultMessage: 'company city' }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'address.country',
        label: intl.formatMessage({ defaultMessage: 'company country' }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'address.state',
        label: intl.formatMessage({ defaultMessage: 'company state' }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'address.zip',
        label: intl.formatMessage({ defaultMessage: 'company zip' }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'name',
        label: intl.formatMessage({ defaultMessage: 'company name' }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      {
        id: 'legalName',
        label: intl.formatMessage({ defaultMessage: 'company legal name' }),
        fallback: '',
        dataType: 'string',
        tag: 'company',
      },
      // Brand attributes
      {
        id: 'name',
        label: intl.formatMessage({ defaultMessage: 'brand name' }),
        fallback: '',
        dataType: 'string',
        tag: 'brand',
      },
    ],
    [intl],
  );
}

